import { useEffect, useState } from 'react';
import { AlertCircle, KeyRound, Library, Link as LinkIcon, Loader2, LogIn, Mail } from 'lucide-react';
import { sessionHubService, HubError, HubMe, HubSummary } from '../../services/sessionHub.service';
import HubShell from './HubShell';

type Step =
  | { name: 'loading' }
  | { name: 'unavailable'; message: string }
  | { name: 'email'; hub: HubSummary }
  | { name: 'code'; hub: HubSummary; email: string }
  | { name: 'in'; me: HubMe };

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

export default function SessionHubPage() {
  const [link] = useState(() => sessionHubService.takeLinkFromUrl());
  const [step, setStep] = useState<Step>({ name: 'loading' });
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!link) {
      setStep({ name: 'unavailable', message: 'Open the link from your email to reach your Session Hub.' });
      return;
    }
    (async () => {
      if (sessionHubService.visitorToken(link)) {
        try {
          setStep({ name: 'in', me: await sessionHubService.me(link) });
          return;
        } catch {
          sessionHubService.signOut(link);
        }
      }
      try {
        setStep({ name: 'email', hub: await sessionHubService.resolve(link) });
      } catch (err) {
        setStep({ name: 'unavailable', message: err instanceof HubError ? err.message : 'We could not open this link. Please try again.' });
      }
    })();
  }, [link]);

  async function sendCode(e: React.FormEvent, hub: HubSummary) {
    e.preventDefault();
    if (!link || !email.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const r = await sessionHubService.requestCode(link, email.trim());
      setNotice(r.message);
      setCode('');
      setStep({ name: 'code', hub, email: email.trim() });
    } catch (err) {
      setError(err instanceof HubError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function verify(e: React.FormEvent, current: { hub: HubSummary; email: string }) {
    e.preventDefault();
    if (!link) return;
    setBusy(true);
    setError(null);
    try {
      await sessionHubService.verify(link, current.email, code.trim());
      setNotice(null);
      setStep({ name: 'in', me: await sessionHubService.me(link) });
    } catch (err) {
      setError(err instanceof HubError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  function signOut() {
    if (!link) return;
    sessionHubService.signOut(link);
    setEmail('');
    setCode('');
    setStep({ name: 'loading' });
    sessionHubService.resolve(link)
      .then((hub) => setStep({ name: 'email', hub }))
      .catch((err) => setStep({ name: 'unavailable', message: err.message }));
  }

  const input = 'w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none';
  const primary = 'w-full flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60 disabled:cursor-not-allowed';
  const secondary = 'w-full flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors';
  if (step.name === 'in' && link) return <HubShell link={link} me={step.me} onSignOut={signOut} />;

  const hub = step.name === 'email' || step.name === 'code' ? step.hub : null;

  return (
    <div className="min-h-screen bg-surface-light overflow-y-auto px-4 py-8 sm:py-16 font-display">
      <meta name="robots" content="noindex" />
      <meta name="referrer" content="no-referrer" />
      <div className="w-full max-w-md mx-auto flex flex-col gap-6">

        <header className="flex items-center gap-4 border-b border-slate-200 pb-6">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary flex-shrink-0">
            <Library size={22} />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-bold text-primary tracking-widest uppercase">Version 1 Co-Creation Session Hub</p>
            <h1 className="text-2xl font-bold tracking-tight text-navy break-words">{hub ? hub.title : 'Session Hub'}</h1>
            {hub && <p className="text-slate-500 text-sm font-medium">Available until {formatDate(hub.expiresAt)}</p>}
          </div>
        </header>

        {step.name === 'loading' && (
          <div className="flex justify-center items-center py-20 text-[#64748b]">
            <Loader2 size={22} className="animate-spin" />
          </div>
        )}

        {step.name === 'unavailable' && (
          <div className="rounded-xl bg-white p-12 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <LinkIcon size={28} className="text-slate-400" />
            </div>
            <p className="text-slate-500 text-sm">This link is not available.</p>
            <p className="text-slate-400 text-xs mt-2">{step.message}</p>
          </div>
        )}

        {(step.name === 'email' || step.name === 'code') && (
          <form
            onSubmit={(e) => (step.name === 'email' ? sendCode(e, step.hub) : verify(e, step))}
            className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden"
          >
            {error && (
              <div className="px-5 py-4 border-b border-[#e2e8f0] bg-red-50 flex gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm font-medium text-red-700">{error}</p>
              </div>
            )}

            {step.name === 'email' ? (
              <div className="p-5">
                <label htmlFor="hub-email" className="block text-sm font-semibold text-navy mb-2 flex items-center gap-1.5">
                  <Mail className="w-4 h-4 text-slate-400" />Your work email
                </label>
                <input
                  id="hub-email" type="email" autoComplete="email" autoFocus maxLength={254}
                  value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" className={input}
                />
                <p className="mt-2 text-xs text-slate-400">Only people your Version 1 contact has added can sign in. We will email you a 6-digit code.</p>
              </div>
            ) : (
              <div className="p-5">
                {notice && <p className="text-sm text-slate-600 mb-4">{notice}</p>}
                <label htmlFor="hub-code" className="block text-sm font-semibold text-navy mb-2 flex items-center gap-1.5">
                  <KeyRound className="w-4 h-4 text-slate-400" />Code sent to {step.email}
                </label>
                <input
                  id="hub-code" inputMode="numeric" autoComplete="one-time-code" autoFocus maxLength={6} pattern="\d{6}"
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="123456"
                  className={`${input} tracking-[0.5em] text-center text-lg`}
                />
                <p className="mt-2 text-xs text-slate-400">The code works for 10 minutes.</p>
              </div>
            )}

            <div className="flex flex-col gap-2 px-5 py-4 border-t border-[#e2e8f0] bg-slate-50/50">
              {step.name === 'email' ? (
                <button type="submit" disabled={!email.trim() || busy} className={primary}>
                  <Mail size={16} /> {busy ? 'Sending…' : 'Email me a code'}
                </button>
              ) : (
                <>
                  <button type="submit" disabled={code.length !== 6 || busy} className={primary}>
                    <LogIn size={16} /> {busy ? 'Checking…' : 'Sign in'}
                  </button>
                  <button type="button" className={secondary} onClick={() => { setError(null); setStep({ name: 'email', hub: step.hub }); }}>
                    Use a different email or send a new code
                  </button>
                </>
              )}
            </div>
          </form>
        )}

      </div>
    </div>
  );
}
